/**
 * 来客受付の管理画面で使う型・問い合わせ(`/api/reception/admin/*`。設計書 §9.4、計画 第 3 段)。
 * クエリキーは `["reception", ...]`。書いたら reception と監査のキャッシュを捨てる。
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useApi, useMe } from "../../lib/useApi";
import { hasFeature } from "../../lib/features";
import { hasRole } from "../../lib/roles";

export const ADMIN = "/api/reception/admin";

export type ReceptionLocation = {
  id: number;
  name: string;
  address: string;
  timezone: string;
  desk_email: string;
  phone_extension: string;
  recovery_contact: string;
  checkin_early_minutes: number;
  checkin_late_minutes: number;
  enabled: boolean;
  /** 受付窓口へのテスト送信が今の宛先・配送設定で受理済みか(端末登録の前提) */
  desk_verified: boolean;
  desk_verified_at: string | null;
  member_count: number;
  created_at: string;
  updated_at: string;
};
export type ReceptionLocationInput = Omit<
  ReceptionLocation,
  "id" | "member_count" | "created_at" | "updated_at" | "desk_verified" | "desk_verified_at"
>;

export type ReceptionMember = {
  id: number;
  location_id: number;
  employee_id: number;
  employee: { id: number; emp_code: string; name: string; status: string } | null;
  role: "operator" | "viewer";
  valid_from: string | null;
  valid_until: string | null;
  active: boolean;
  created_by_sub: string;
  created_at: string | null;
};

export type ReceptionSetting = {
  key: string;
  value: Record<string, unknown>;
  stored: Record<string, unknown>;
  updated_at: string | null;
  /** "*" はキー全体が suite:admin、配列はその欄だけ */
  suite_admin_only: "*" | string[];
};

export type ConsentVersion = {
  id: number;
  version: string;
  body_ja: string;
  body_en: string;
  effective_from: string;
  created_by_sub: string;
  created_at: string | null;
  current: boolean;
  future: boolean;
};

export type ChannelResult = {
  channel: string;
  target: string;
  outcome: string;
  ok: boolean;
  detail: string;
  stage: string;
};

/** ボタンの出し分け(権限の真実は API 側)。suite:admin は retention と同一オリジン例外も書ける。 */
export function useReceptionPerms() {
  const me = useMe();
  return {
    canAdmin: hasRole(me.data, "reception:admin"),
    isSuiteAdmin: me.data?.is_suite_admin ?? false,
    hasCore: hasFeature(me.data, "reception.core"),
    readOnly: me.data?.license.read_only ?? true,
  };
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function useInvalidateReception() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["reception"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  }, [qc]);
}

/** JSON を送る(`api.request` の薄い包み)。 */
export function useSend() {
  const api = useApi();
  return useCallback(
    <T>(method: string, path: string, body?: unknown) =>
      api.request<T>(ADMIN + path, {
        method,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      }),
    [api],
  );
}

export function useAdminGet<T>(key: unknown[], path: string, enabled = true) {
  const api = useApi();
  const { canAdmin, hasCore } = useReceptionPerms();
  return useQuery({
    queryKey: ["reception", "admin", ...key],
    queryFn: () => api.request<T>(ADMIN + path),
    enabled: enabled && canAdmin && hasCore,
  });
}

export const useLocations = () => useAdminGet<{ items: ReceptionLocation[] }>(["locations"], "/locations");

export const useMembers = (locationId: number | null) =>
  useAdminGet<{ items: ReceptionMember[] }>(
    ["members", locationId],
    `/locations/${locationId ?? 0}/members`,
    locationId !== null,
  );

export const useSettings = () => useAdminGet<{ items: ReceptionSetting[] }>(["settings"], "/settings");

export const useConsentVersions = () =>
  useAdminGet<{ items: ConsentVersion[]; current_id: number | null }>(["consent"], "/consent-versions");

export type ReceptionDevice = {
  id: number;
  location_id: number;
  name: string;
  platform: string;
  orientation: "landscape" | "portrait";
  camera_side: "front" | "back";
  app_version: string | null;
  online: boolean;
  last_heartbeat_at: string | null;
  battery: number | null;
  queue_len: number | null;
  token_expires_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  queue_recovery_until: string | null;
  created_at: string | null;
};

export type EnrollCode = {
  code: string;
  expires_at: string;
  location_id: number;
  device_name: string;
  device_id: number | null;
};

export const useDevices = (locationId: number | null) =>
  useAdminGet<{ items: ReceptionDevice[]; total: number }>(
    ["devices", locationId],
    locationId === null ? "/devices" : `/devices?location_id=${locationId}`,
  );

/** 保有期間(第 7 段)。日数を変えられるのは suite:admin(設定画面の reception.retention) */
export type RetentionInfo = {
  retention_days: number;
  cancelled_days: number;
  fixed: {
    delivery_days: number;
    notification_days: number;
    kiosk_event_days: number;
    quarantine_days: number;
    ticket_days: number;
    throttle_days: number;
  };
  can_edit_days: boolean;
};

/** 今実行したら処理する件数(キー → 件数)。知らないキーも来うる */
export type RetentionCounts = Record<string, number>;

export const useRetention = () => useAdminGet<RetentionInfo>(["retention"], "/retention");

export const useRetentionPreview = () =>
  useAdminGet<{ at: string; counts: RetentionCounts }>(["retention", "preview"], "/retention/preview");
